import { ColorPair, HEX } from './colors';
import { ColorOverrides } from './theme';

/**
 * Interface for RGB color object
 */
export interface RGBColor {
  r: number;
  g: number;
  b: number;
  a?: number;
}

/**
 * Converts a hex color string to RGB components
 * @param {string} hex - The hex color string (e.g., "#FF0000" or "FF0000")
 * @returns {RGBColor} Object with r, g, b components
 */
function hexToRgb(hex: HEX): RGBColor {
  // Remove the # if present
  const hexString = hex.replace(/^#/, '');

  // Parse the hex values
  const r = parseInt(hexString.substring(0, 2), 16);
  const g = parseInt(hexString.substring(2, 4), 16);
  const b = parseInt(hexString.substring(4, 6), 16);

  return { r, g, b };
}

/**
 * Converts an RGB color object to a hex color string
 * @param {RGBColor} rgb - The RGB color object
 * @returns {string} The hex color string (e.g., "#FF0000")
 */
function rgbToHex(rgb: RGBColor): HEX {
  return `#${rgb.r.toString(16).padStart(2, '0')}${rgb.g
    .toString(16)
    .padStart(2, '0')}${rgb.b.toString(16).padStart(2, '0')}`;
}

/**
 * Lerps from the source color to the layer color based on the lerp ratio
 * @param {HEX} sourceColor - The source color
 * @param {HEX} layerColor - The layer color
 * @param {number} lerpRatio - The ratio of the lerp
 * @returns {HEX} The lerped color
 */
export const colorLerp = (
  sourceColor: HEX,
  layerColor: HEX,
  lerpRatio: number
): HEX => {
  const source =
    typeof sourceColor === 'string' ? hexToRgb(sourceColor) : sourceColor;
  const layer =
    typeof layerColor === 'string' ? hexToRgb(layerColor) : layerColor;

  const { r: sr, g: sg, b: sb } = source;
  const { r: lr, g: lg, b: lb } = layer;

  const diffR = (lr - sr) * lerpRatio;
  const diffG = (lg - sg) * lerpRatio;
  const diffB = (lb - sb) * lerpRatio;

  const resultR = Math.round(sr + diffR);
  const resultG = Math.round(sg + diffG);
  const resultB = Math.round(sb + diffB);

  return rgbToHex({ r: resultR, g: resultG, b: resultB });
};

type BrandColors = {
  'brand/default': ColorPair;
  'brand/tint': ColorPair;
  'brand/accent': ColorPair;
  'brand/surface': ColorPair;
};

export const generateBrandColors = (colors: ColorOverrides): BrandColors => {
  const brandBase = colors['brand/default'];

  if (!brandBase || !brandBase.light || !brandBase.dark) {
    throw new Error('Brand base color is required');
  }

  let brandTint = colors['brand/tint'];
  let brandAccent = colors['brand/accent'];
  let brandSurface = colors['brand/surface'];

  if (!brandTint || !brandTint.light || !brandTint.dark) {
    brandTint = {
      light: colorLerp(brandBase.light, '#ffffff', 0.6),
      dark: colorLerp(brandBase.dark, '#000000', 0.15),
    };
  }

  if (!brandAccent || !brandAccent.light || !brandAccent.dark) {
    brandAccent = {
      light: colorLerp(brandBase.light, '#000000', 0.2),
      dark: colorLerp(brandBase.dark, '#ffffff', 0.6),
    };
  }

  if (!brandSurface || !brandSurface.light || !brandSurface.dark) {
    brandSurface = {
      light: colorLerp(brandBase.light, '#ffffff', 0.9),
      dark: colorLerp(brandBase.dark, '#000000', 0.6),
    };
  }

  return {
    'brand/default': brandBase as ColorPair,
    'brand/tint': brandTint as ColorPair,
    'brand/accent': brandAccent as ColorPair,
    'brand/surface': brandSurface as ColorPair,
  };
};
